/**
 * @file product-element-meta.ts
 * @description Элементы данных товара в редакторе секции (phase19.16 Р2а
 * «Каталог и товар»; uiux-design-r2a §3; SB34): значки и подписи 13 видов
 * (`PRODUCT_ELEMENT_INFO` из shared — название, подсказка, обязательность),
 * знак обязательного «(з)» и тексты замков.
 *
 * Реестр (OCP): новый элемент товара в shared — новая строка значка здесь;
 * `Record<ProductElementKind, …>` не даст собрать кабинет, если вид забыли.
 */

import {
  PRODUCT_ELEMENT_INFO,
  PRODUCT_ELEMENT_KINDS,
  isProductRequiredKind,
  type ProductElementKind,
  type ProductRequiredKind,
  SITE_LIMITS,
} from "@chaiberi/shared";
import {
  Award,
  BadgeCheck,
  ChevronsRight,
  ClipboardList,
  FileText,
  GalleryHorizontal,
  Heading1,
  ListTree,
  MessageSquareQuote,
  ShoppingCart,
  Store,
  Tags,
  TextQuote,
  type LucideIcon,
} from "lucide-react";

import type { ElementKindMeta } from "./section-meta";

const PRODUCT_ICONS: Readonly<Record<ProductElementKind, LucideIcon>> = {
  "product-gallery": GalleryHorizontal,
  "product-title": Heading1,
  "product-buy": ShoppingCart,
  "product-badges": Award,
  "product-short": TextQuote,
  "product-description": FileText,
  "product-facts": ClipboardList,
  "product-specs": ListTree,
  "product-tags": Tags,
  "product-reviews": MessageSquareQuote,
  "product-related": BadgeCheck,
  breadcrumbs: ChevronsRight,
  "product-seller": Store,
};

/**
 * Названия в кабинете, где слово из shared совпадает с общим элементом:
 * «Галерея» товара и «Галерея» фото медиатеки — разные элементы (приёмка Р2а).
 */
const LABEL_OVERRIDE: Readonly<Partial<Record<ProductElementKind, string>>> = {
  "product-gallery": "Галерея товара",
};

/** Записи элементов товара для общего реестра `ELEMENT_KIND_META`. */
export const PRODUCT_ELEMENT_META: Readonly<Record<ProductElementKind, ElementKindMeta>> = Object.fromEntries(
  PRODUCT_ELEMENT_KINDS.map((kind) => [kind, { label: LABEL_OVERRIDE[kind] ?? PRODUCT_ELEMENT_INFO[kind].label, hint: PRODUCT_ELEMENT_INFO[kind].hint, icon: PRODUCT_ICONS[kind] }]),
) as Record<ProductElementKind, ElementKindMeta>;

/** Почему обязательный элемент нельзя удалить (uiux-design-r2a §6). */
export const REQUIRED_REMOVE_TEXT: Readonly<Record<ProductRequiredKind, string>> = {
  "product-title": "Название нельзя удалить: это главный заголовок страницы товара. Переставьте элемент.",
  "product-buy": "Цену и покупку нельзя удалить: покупатель по закону должен видеть цену и условия покупки. Переставьте элемент.",
  "product-facts": "Сведения о товаре нельзя удалить: состав, сроки и производитель по закону видны всегда. Переставьте элемент.",
  // «Продавец» с 2026-10-11 не обязателен (SB34): реквизиты есть в юридической строке подвала.
};

/** Текст причины для удаления обязательного элемента. */
export function requiredRemoveText(kind: string): string {
  return isProductRequiredKind(kind) ? REQUIRED_REMOVE_TEXT[kind] : "Обязательный элемент нельзя удалить — переставьте его.";
}

/** Снять устройство у обязательного элемента нельзя. */
export const REQUIRED_DEVICES_TEXT = "Обязательный элемент виден на всех устройствах.";

/** Блок с обязательными элементами виден на всех устройствах. */
export const REQUIRED_BLOCK_DEVICES_TEXT = "В блоке обязательные элементы — он виден на всех устройствах.";

/** Скрыть блок с обязательными элементами нельзя. */
export const REQUIRED_BLOCK_HIDE_TEXT = "В блоке обязательные элементы — скрыть его нельзя.";

/** В шаблоне страницы товара свой лимит блоков (SB64). */
export const TEMPLATE_BLOCKS_FULL_TEXT = `В шаблоне страницы товара ${SITE_LIMITS.productTemplateSections} блоков — больше не поместится. Удалите лишний или объедините блоки.`;

/** «Дублировать» в шаблоне: сервер вырезает из копии элементы данных товара. */
export const TEMPLATE_DUPLICATE_HINT = "Копия без элементов товара — они в шаблоне только по одному.";

/** Элемент данных товара повторить нельзя: в шаблоне он один. */
export const PRODUCT_ELEMENT_DUPLICATE_TEXT = "Этот элемент в шаблоне может быть только один.";
